import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const s = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── Correlation Visualizer ──
function CorrelationViz() {
  const [corr, setCorr] = useState(0);
  const steps = 60;
  const baseA = Array.from({ length: steps }, (_, i) => Math.sin(i * 0.3) * 30 + 50);

  function genB(correlation) {
    return baseA.map((v, i) => {
      const noise = (Math.random() - 0.5) * 40 * (1 - Math.abs(correlation));
      const inverted = 100 - v;
      return corr >= 0
        ? v * Math.abs(corr) + (50 + noise) * (1 - Math.abs(corr))
        : inverted * Math.abs(corr) + (50 + noise) * (1 - Math.abs(corr));
    });
  }

  const [seriesB] = useState(() => genB(corr));
  const [bData, setBData] = useState(seriesB);

  function handleCorr(val) {
    setCorr(val);
    const c = parseFloat(val);
    const newB = baseA.map((v, i) => {
      const seed = ((i * 9301 + 49297) % 233280) / 233280;
      const noise = (seed - 0.5) * 40 * (1 - Math.abs(c));
      const inverted = 100 - v;
      return c >= 0
        ? v * Math.abs(c) + (50 + noise) * (1 - Math.abs(c))
        : inverted * Math.abs(c) + (50 + noise) * (1 - Math.abs(c));
    });
    setBData(newB);
  }

  const corrNum = parseFloat(corr);
  const level = corrNum >= 0.7 ? { label: "Strong Positive — No Benefit", color: red }
    : corrNum >= 0.3 ? { label: "Moderate Positive — Slight Benefit", color: gold }
    : corrNum >= -0.3 ? { label: "Low / No Correlation — Good Benefit", color: blue2 }
    : corrNum >= -0.7 ? { label: "Moderate Negative — Strong Benefit", color: green }
    : { label: "Strong Negative — Maximum Benefit", color: "#4ade80" };

  const svgH = 100, svgW = 500;
  function pts(data) {
    return data.map((v, i) => `${(i / (steps - 1)) * svgW},${svgH - (v / 100) * svgH}`).join(" ");
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>📈 Correlation & Diversification Visualizer</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Drag the correlation slider to see how two assets move in relation to each other — and the diversification impact on portfolio risk.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Correlation</label>
        <input type="range" min={-1} max={1} step={0.1} value={corr} onChange={e => handleCorr(parseFloat(e.target.value))} style={{ flex: 1, accentColor: gold }} />
        <span style={{ fontFamily: "monospace", fontSize: "1rem", color: gold, width: 50, textAlign: "right" }}>{parseFloat(corr).toFixed(1)}</span>
      </div>
      <div style={{ overflowX: "auto", marginBottom: 14 }}>
        <svg width={svgW} height={svgH + 4} style={{ display: "block" }}>
          <polyline points={pts(baseA)} fill="none" stroke={blue2} strokeWidth={2} opacity={0.9} />
          <polyline points={pts(bData)} fill="none" stroke={gold} strokeWidth={2} opacity={0.9} />
        </svg>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
        {[["Asset A", blue2], ["Asset B", gold]].map(([lbl, col]) => (
          <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 20, height: 3, background: col, borderRadius: 2 }} />
            <span style={{ fontSize: "0.78rem", color: muted }}>{lbl}</span>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${level.color}44`, borderRadius: 8, padding: "14px 18px" }}>
        <div style={{ fontSize: "0.82rem", fontWeight: 700, color: level.color, marginBottom: 4 }}>{level.label}</div>
        <div style={{ fontSize: "0.8rem", color: muted, lineHeight: 1.7 }}>
          {corrNum >= 0.7 ? "The two assets move almost identically. Adding the second asset offers virtually no risk reduction — you're just doubling your exposure."
            : corrNum >= 0.3 ? "Moderate positive correlation — some diversification benefit, but the assets tend to rise and fall together."
            : corrNum >= -0.3 ? "Little to no correlation — the assets are largely independent. A good pairing for diversification."
            : corrNum >= -0.7 ? "Moderate negative correlation — when one falls, the other tends to rise. Meaningful risk reduction."
            : "Near-perfect negative correlation — maximum diversification benefit. When one zigs, the other zags."}
        </div>
      </div>
    </div>
  );
}

// ── Asset Class Cards ──
function AssetClassCards() {
  const [active, setActive] = useState(null);
  const classes = [
    {
      icon: "💵", label: "Cash / Money Market", color: green, risk: "Low",
      role: "Safety of principal and immediate liquidity.",
      risks: ["Inflation risk — returns may not keep pace with cost of living", "Opportunity cost — misses out on higher returns from other asset classes"],
      examples: ["Treasury Bills (T-Bills)", "High-Interest Savings Accounts", "Money Market Funds"],
      retPotential: 15, riskLevel: 10,
    },
    {
      icon: "📄", label: "Fixed Income (Bonds)", color: blue2, risk: "Moderate",
      role: "Regular income and capital preservation.",
      risks: ["Interest rate risk — rising rates reduce bond prices", "Credit risk — issuer may default on payments"],
      examples: ["Government Bonds", "Corporate Bonds", "Preferred Shares"],
      retPotential: 45, riskLevel: 35,
    },
    {
      icon: "📈", label: "Equities (Stocks)", color: gold, risk: "High",
      role: "Long-term capital growth.",
      risks: ["Market volatility — prices fluctuate significantly", "Business risk — company may underperform or fail"],
      examples: ["Common Shares", "Equity Mutual Funds", "ETFs"],
      retPotential: 85, riskLevel: 80,
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
        {classes.map((c, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? c.color + "55" : border}`, borderRadius: 8, padding: 22, cursor: "pointer", transition: "all 0.2s", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${c.color},transparent)` }} />
            <div style={{ fontSize: "1.6rem", marginBottom: 10 }}>{c.icon}</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: "0.8rem", color: muted, marginBottom: 12, lineHeight: 1.6 }}>{c.role}</div>
            <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
              {[["Return Potential", c.retPotential, gold], ["Risk Level", c.riskLevel, c.color]].map(([lbl, val, col]) => (
                <div key={lbl}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.68rem", color: muted, marginBottom: 3 }}>
                    <span>{lbl}</span><span style={{ color: col }}>{val}%</span>
                  </div>
                  <div style={{ height: 4, background: navy3, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${val}%`, background: col, borderRadius: 2, transition: "width 0.6s ease" }} />
                  </div>
                </div>
              ))}
            </div>
            {active === i && (
              <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12, display: "grid", gap: 8 }}>
                <div>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: red, marginBottom: 6 }}>Key Risks</div>
                  {c.risks.map((r, j) => <div key={j} style={{ fontSize: "0.8rem", color: muted, paddingLeft: 10, marginBottom: 4, lineHeight: 1.6 }}>• {r}</div>)}
                </div>
                <div>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: c.color, marginBottom: 6 }}>Examples</div>
                  {c.examples.map((ex, j) => <div key={j} style={{ fontSize: "0.8rem", color: textCol, paddingLeft: 10, marginBottom: 4 }}>→ {ex}</div>)}
                </div>
              </div>
            )}
            <div style={{ fontSize: "0.72rem", color: active === i ? c.color : muted, marginTop: 8 }}>{active === i ? "↑ Collapse" : "↓ Expand"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Strategic vs Tactical ──
function AllocationToggle() {
  const [mode, setMode] = useState("strategic");
  const configs = {
    strategic: {
      label: "Strategic", color: blue2,
      equity: 60, bond: 30, cash: 10,
      desc: "The long-term 'base' mix designed to meet the client's goals and risk tolerance. Rebalanced periodically — not changed based on market conditions.",
      tag: "Long-Term Foundation",
    },
    tactical: {
      label: "Tactical (Bull Market)", color: gold,
      equity: 75, bond: 20, cash: 5,
      desc: "A short-term deviation from the strategic mix to capitalize on a market opportunity — here, overweighting equities during a bull market. Always reverts to strategic.",
      tag: "Short-Term Deviation",
    },
  };
  const cfg = configs[mode];
  const segments = [
    { label: "Equities", pct: cfg.equity, color: gold },
    { label: "Fixed Income", pct: cfg.bond, color: blue2 },
    { label: "Cash", pct: cfg.cash, color: green },
  ];

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 20 }}>⚖️ Strategic vs. Tactical Asset Allocation</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
        {Object.entries(configs).map(([key, val]) => (
          <button key={key} onClick={() => setMode(key)} style={{ padding: "9px 20px", background: mode === key ? navy4 : navy3, border: `1px solid ${mode === key ? val.color + "55" : border}`, borderBottom: mode === key ? `2px solid ${val.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: mode === key ? val.color : muted, transition: "all 0.2s" }}>{val.label}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "center" }}>
        {/* Stacked bar */}
        <div>
          <div style={{ height: 28, borderRadius: 6, overflow: "hidden", display: "flex", marginBottom: 12 }}>
            {segments.map((seg, i) => (
              <div key={i} style={{ width: `${seg.pct}%`, background: seg.color, transition: "width 0.5s ease", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {seg.pct >= 12 && <span style={{ fontSize: "0.7rem", fontWeight: 700, color: navy }}>{seg.pct}%</span>}
              </div>
            ))}
          </div>
          {segments.map((seg, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
              <span style={{ fontSize: "0.82rem", color: textCol }}>{seg.label}</span>
              <span style={{ fontFamily: "monospace", fontSize: "0.82rem", color: seg.color, marginLeft: "auto" }}>{seg.pct}%</span>
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: cfg.color, marginBottom: 8 }}>{cfg.tag}</div>
          <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.7 }}>{cfg.desc}</div>
        </div>
      </div>
    </div>
  );
}

// ── Active vs Passive ──
function ManagementComparison() {
  const [active, setActive] = useState(0);
  const styles = [
    {
      label: "⚡ Active", name: "Active Management", color: gold,
      tagline: "Goal: Beat the benchmark. Generate Alpha.",
      rows: [
        ["Strategy", "Research, forecasting, market timing, security selection"],
        ["Cost (MER)", "Higher — research teams, frequent trading, analyst costs"],
        ["Benchmark", "Seeks to outperform (e.g., beat the S&P/TSX 60)"],
        ["Approaches", "Top-Down Analysis and Bottom-Up Analysis"],
        ["Risk", "Manager risk — underperformance is possible and common"],
      ],
      subItems: [
        { name: "🔭 Top-Down Analysis", desc: "Starts with the macroeconomy → then industries → then specific companies. Macro drives security selection." },
        { name: "🔬 Bottom-Up Analysis", desc: "Focuses on individual company fundamentals regardless of the economy. Looks for strong companies in any environment." },
      ],
      pills: ["Alpha Seeking", "Higher MER", "Manager Skill Required"],
    },
    {
      label: "🧘 Passive", name: "Passive Management", color: green,
      tagline: "Goal: Match the benchmark. Minimize cost.",
      rows: [
        ["Strategy", "Buy and hold the same securities as the index — no selection"],
        ["Cost (MER)", "Lower — no research or active trading required"],
        ["Benchmark", "Seeks to replicate (e.g., S&P/TSX 60, S&P 500)"],
        ["Vehicles", "Index Funds and Exchange-Traded Funds (ETFs)"],
        ["Advantage", "Consistent market return; most active managers underperform long-term"],
      ],
      subItems: [
        { name: "📦 Index Funds", desc: "Mutual funds that track a market index. Typically lower MER than actively managed funds. Priced at end of day." },
        { name: "🔄 ETFs (Exchange-Traded Funds)", desc: "Like index funds but trade on an exchange like stocks — intraday pricing. Often even lower MER than index mutual funds." },
      ],
      pills: ["Index Replication", "Lower MER", "No Manager Risk"],
    },
  ];
  const s = styles[active];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2 }}>
        {styles.map((st, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 20px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? st.color + "44" : border}`, borderBottom: active === i ? `2px solid ${st.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? st.color : muted, transition: "all 0.2s" }}>{st.label}</button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${s.color}22`, borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", color: white, marginBottom: 4 }}>{s.name}</div>
        <div style={{ fontSize: "0.82rem", color: s.color, fontStyle: "italic", marginBottom: 16 }}>{s.tagline}</div>
        <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
          {s.rows.map(([lbl, val], i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "11px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: s.color, width: 110, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.85rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          {s.subItems.map((item, i) => (
            <div key={i} style={{ background: navy3, borderLeft: `3px solid ${s.color}`, borderRadius: 6, padding: "12px 16px" }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white, marginBottom: 4 }}>{item.name}</div>
              <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65 }}>{item.desc}</div>
            </div>
          ))}
        </div>
        <div>{s.pills.map((p, i) => <Pill key={i} text={p} color={s.color} />)}</div>
      </div>
    </div>
  );
}

// ── Beta Visualizer ──
function BetaViz() {
  const [beta, setBeta] = useState(1.0);
  const mktReturn = 10;
  const stockReturn = mktReturn * beta;
  const mktDown = -8;
  const stockDown = mktDown * beta;
  const category = beta > 1.2 ? { label: "Aggressive (High Volatility)", color: red }
    : beta > 0.8 ? { label: "Market-Neutral", color: blue2 }
    : { label: "Defensive (Low Volatility)", color: green };

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>📡 Beta Simulator</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Beta measures a security's sensitivity to market movements. Drag the slider to see how a stock with this beta would perform in both a rising and falling market.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Beta</label>
        <input type="range" min={0.2} max={2.0} step={0.1} value={beta} onChange={e => setBeta(parseFloat(e.target.value))} style={{ flex: 1, accentColor: gold }} />
        <span style={{ fontFamily: "monospace", fontSize: "1rem", color: gold, width: 40, textAlign: "right" }}>{beta.toFixed(1)}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Market +10%", marketVal: mktReturn, stockVal: stockReturn, upside: true },
          { label: "Market −8%", marketVal: mktDown, stockVal: stockDown, upside: false },
        ].map((scenario, i) => (
          <div key={i} style={{ background: navy4, borderRadius: 8, padding: "16px 14px" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 10 }}>{scenario.label}</div>
            {[{ lbl: "Market", val: scenario.marketVal, col: blue2 }, { lbl: "This Stock", val: scenario.stockVal, col: scenario.upside ? gold : red }].map((row, j) => (
              <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: "0.78rem", color: muted }}>{row.lbl}</span>
                <span style={{ fontFamily: "monospace", fontSize: "0.9rem", fontWeight: 700, color: row.col }}>{row.val >= 0 ? "+" : ""}{row.val.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        ))}{/* Beta label card */}
        <div style={{ background: navy4, border: `1px solid ${category.color}44`, borderRadius: 8, padding: "16px 14px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
          <div style={{ fontFamily: "monospace", fontSize: "2rem", color: gold, fontWeight: 700 }}>{beta.toFixed(1)}</div>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: category.color, marginTop: 6 }}>{category.label}</div>
        </div>
      </div>
      <div style={{ background: navy4, border: `1px solid ${category.color}33`, borderRadius: 8, padding: "14px 18px", fontSize: "0.82rem", color: textCol, lineHeight: 1.7 }}>
        {beta === 1.0 ? "Beta of exactly 1.0 — this security moves in perfect lockstep with the market."
          : beta > 1.0 ? `Beta of ${beta.toFixed(1)} — this security is ${((beta - 1) * 100).toFixed(0)}% more volatile than the market. It amplifies both gains and losses.`
          : `Beta of ${beta.toFixed(1)} — this security is ${((1 - beta) * 100).toFixed(0)}% less volatile than the market. It cushions losses in downturns.`}
      </div>
    </div>
  );
}

// ── Calculators ──
function RealReturnCalc() {
  const [nominal, setNominal] = useState(8.5);
  const [inflation, setInflation] = useState(2.0);
  const [result, setResult] = useState(null);
  function calc() {
    const n = parseFloat(nominal), inf = parseFloat(inflation);
    setResult({ realReturn: (n - inf).toFixed(2), n, inf });
  }
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Real Rate of Return</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.82rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "6px 14px", borderRadius: 4, display: "inline-block", marginBottom: 20 }}>
        Real Return = Nominal Return − Inflation Rate
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[["Nominal Return (%)", nominal, setNominal], ["Inflation Rate (%)", inflation, setInflation]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.92rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 6 }}>Real Rate of Return</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 900, color: parseFloat(result.realReturn) >= 0 ? green : red }}>{result.realReturn}%</div>
          <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: textCol, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${border}` }}>
            → {result.n}% − {result.inf}% = {result.realReturn}%
          </div>
          <div style={{ fontSize: "0.8rem", color: muted, marginTop: 6 }}>
            {parseFloat(result.realReturn) > 0 ? "Positive real return — purchasing power is growing after inflation." : "Negative real return — inflation is eroding purchasing power."}
          </div>
        </div>
      )}
    </div>
  );
}

function PortfolioReturnCalc() {
  const [funds, setFunds] = useState([{ weight: 40, ret: 10 }, { weight: 60, ret: 5 }]);
  const [result, setResult] = useState(null);

  function updateFund(i, field, val) {
    const updated = funds.map((f, idx) => idx === i ? { ...f, [field]: parseFloat(val) || 0 } : f);
    setFunds(updated);
    setResult(null);
  }

  function calc() {
    const totalWeight = funds.reduce((s, f) => s + f.weight, 0);
    if (Math.abs(totalWeight - 100) > 0.1) { alert(`Weights must sum to 100%. Current total: ${totalWeight}%`); return; }
    const contributions = funds.map(f => ({ contribution: (f.weight / 100) * f.ret, weight: f.weight, ret: f.ret }));
    const total = contributions.reduce((s, c) => s + c.contribution, 0);
    setResult({ contributions, total: total.toFixed(2) });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Portfolio Return (Weighted Average)</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.82rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "6px 14px", borderRadius: 4, display: "inline-block", marginBottom: 20 }}>
        Portfolio Return = Σ (Weight × Return) for each holding
      </div>
      <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr", gap: 8 }}>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", padding: "0 0 4px" }}>Fund</div>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", padding: "0 0 4px" }}>Weight (%)</div>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", padding: "0 0 4px" }}>Return (%)</div>
        </div>
        {funds.map((f, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr", gap: 8, alignItems: "center" }}>
            <div style={{ fontSize: "0.8rem", color: gold, fontWeight: 700 }}>Fund {String.fromCharCode(65 + i)}</div>
            {["weight", "ret"].map(field => (
              <input key={field} type="number" value={f[field]} onChange={e => updateFund(i, field, e.target.value)}
                style={{ background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none", width: "100%" }} />
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 0, flexWrap: "wrap" }}>
        <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
        <button onClick={() => { setFunds([...funds, { weight: 0, ret: 0 }]); setResult(null); }} style={{ background: "transparent", color: blue2, border: `1px solid rgba(59,130,246,.3)`, padding: "12px 20px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>+ Add Fund</button>
      </div>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 6 }}>Portfolio Return</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 900, color: white }}>{result.total}%</div>
          <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0", borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            {result.contributions.map((c, i) => (
              <li key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>
                Fund {String.fromCharCode(65 + i)}: {c.weight}% × {c.ret}% = <strong style={{ color: blue2 }}>{c.contribution.toFixed(2)}%</strong>
              </li>
            ))}
            <li style={{ fontFamily: "monospace", fontSize: "0.82rem", color: white, padding: "8px 0 0", borderTop: `1px solid ${border}`, marginTop: 4, display: "flex", gap: 8 }}>
              <span style={{ color: gold }}>= </span>Total: {result.total}%
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

function SharpeCalc() {
  const [portRet, setPortRet] = useState(12);
  const [rfRate, setRfRate] = useState(2);
  const [stdDev, setStdDev] = useState(10);
  const [result, setResult] = useState(null);
  function calc() {
    const p = parseFloat(portRet), r = parseFloat(rfRate), sd = parseFloat(stdDev);
    const excess = p - r;
    const sharpe = (excess / sd).toFixed(2);
    const rating = parseFloat(sharpe) >= 1.5 ? "Excellent" : parseFloat(sharpe) >= 1.0 ? "Good" : parseFloat(sharpe) >= 0.5 ? "Acceptable" : "Poor";
    const ratingColor = parseFloat(sharpe) >= 1.5 ? green : parseFloat(sharpe) >= 1.0 ? blue2 : parseFloat(sharpe) >= 0.5 ? gold : red;
    setResult({ sharpe, excess, p, r, sd, rating, ratingColor });
  }
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Sharpe Ratio Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.82rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "6px 14px", borderRadius: 4, display: "inline-block", marginBottom: 20 }}>
        Sharpe Ratio = (Portfolio Return − Risk-Free Rate) ÷ Standard Deviation
      </div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 16, lineHeight: 1.65 }}>
        Measures <strong style={{ color: textCol }}>risk-adjusted return</strong> — how much excess return is earned per unit of risk. Higher is better.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 16 }}>
        {[["Portfolio Return (%)", portRet, setPortRet], ["Risk-Free Rate (%)", rfRate, setRfRate], ["Standard Deviation (%)", stdDev, setStdDev]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.92rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold }}>Sharpe Ratio</div>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 100, color: result.ratingColor, background: `${result.ratingColor}18`, border: `1px solid ${result.ratingColor}33` }}>{result.rating}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 900, color: white }}>{result.sharpe}</div>
          <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0", borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            {[
              `Excess return: ${result.p}% − ${result.r}% = ${result.excess.toFixed(2)}%`,
              `Divide by risk: ${result.excess.toFixed(2)}% ÷ ${result.sd}% = ${result.sharpe}`,
              `Interpretation: ${result.sharpe} units of return earned per unit of risk taken`,
            ].map((step, i) => (
              <li key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which correlation coefficient represents the maximum diversification benefit?", opts: ["+1.0", "+0.5", "0.0", "−1.0"], correct: 3, fb: { y: "Correct! A correlation of −1.0 means assets move in perfectly opposite directions — when one rises, the other falls by exactly the same amount. This provides maximum portfolio risk reduction.", n: "Not quite. Negative correlation (−1.0) provides maximum diversification. Assets that move in opposite directions offset each other's volatility, reducing overall portfolio risk." } },
  { q: "Strategic Asset Allocation is best described as:", opts: ["Moving 100% into cash when the market drops", "The long-term mix of assets chosen to meet client goals and risk tolerance", "Picking the 'hot' stock of the day", "Short-term trading to beat the market"], correct: 1, fb: { y: "Correct! Strategic Asset Allocation is the long-term 'base' mix designed to match the client's goals, time horizon, and risk tolerance. It's rebalanced periodically — not changed based on market noise.", n: "Not quite. Strategic Asset Allocation is a long-term foundation — not reactive market timing. It's designed to reflect the client's goals and is only adjusted periodically." } },
  { q: "Which portfolio management style seeks to replicate the performance of a market index?", opts: ["Active Management", "Top-Down Analysis", "Passive Management", "Bottom-Up Analysis"], correct: 2, fb: { y: "Correct! Passive management replicates a market index (e.g., S&P/TSX 60) using index funds or ETFs. It accepts market returns with lower costs and no manager risk.", n: "Not quite. Passive management seeks to match an index, not beat it. Active management (top-down and bottom-up) is where managers try to outperform." } },
  { q: "If a stock has a Beta of 1.5, how is it expected to behave relative to the market?", opts: ["It will be 50% less volatile than the market", "It will be 50% more volatile than the market", "It will move exactly with the market", "It will move in the opposite direction of the market"], correct: 1, fb: { y: "Correct! Beta of 1.5 means the stock moves 1.5× the market. If the market rises 10%, the stock should rise ~15%. If the market falls 8%, the stock should fall ~12%.", n: "Not quite. Beta > 1 means more volatile than the market. Beta of 1.5 = 50% more volatile. Beta < 1 = less volatile. Beta = 1.0 = matches the market exactly." } },
  { q: "An investor rebalances their portfolio by selling the asset class that has risen and buying the one that has fallen. This enforces which discipline?", opts: ["Market Timing", "Buy High, Sell Low", "Buy Low, Sell High", "Tax Loss Selling"], correct: 2, fb: { y: "Correct! Rebalancing systematically forces you to sell what has become expensive (risen) and buy what is now cheap (fallen) — enforcing 'buy low, sell high' discipline.", n: "Not quite. Rebalancing means selling outperformers and buying underperformers — which systematically enforces 'buy low, sell high.'" } },
  { q: "'Top-Down' analysis begins with an examination of:", opts: ["Specific companies", "The Global and Domestic Economy", "Industry sectors only", "Technical price charts"], correct: 1, fb: { y: "Correct! Top-Down analysis starts with the macro economy, narrows to industry sectors, then identifies specific securities. Economy → Industry → Company.", n: "Not quite. Top-Down flows from macro to micro: Economy → Industry → Company. Bottom-Up starts with individual companies regardless of macro conditions." } },
  { q: "Standard Deviation is a measure of:", opts: ["Credit Risk", "Inflation Risk", "Volatility (Total Risk)", "Liquidity Risk"], correct: 2, fb: { y: "Correct! Standard deviation measures the variability of returns around the average — higher standard deviation means more volatile returns and higher total risk.", n: "Not quite. Standard deviation measures volatility — how much returns deviate from their average. Higher SD = more unpredictable, riskier investment." } },
  { q: "Which asset class is primarily used for capital preservation and liquidity?", opts: ["Equities", "Real Estate", "Money Market / Cash", "High Yield Bonds"], correct: 2, fb: { y: "Correct! Money market / cash instruments (T-bills, HISAs) prioritize safety of principal and immediate liquidity — at the cost of lower returns and inflation risk.", n: "Not quite. Cash / money market instruments are for capital preservation and liquidity. Equities are for growth; bonds for income." } },
  { q: "A portfolio returned 12%, the risk-free rate is 2%, and the standard deviation is 10%. What is the Sharpe Ratio?", opts: ["0.8", "1.0", "1.2", "1.4"], correct: 1, fb: { y: "Correct! Sharpe Ratio = (12% − 2%) ÷ 10% = 10% ÷ 10% = 1.0. The portfolio earned 1 unit of return for every 1 unit of risk taken.", n: "Not quite. Sharpe Ratio = (Portfolio Return − Risk-Free Rate) ÷ Standard Deviation = (12 − 2) ÷ 10 = 10 ÷ 10 = 1.0." } },
  { q: "The Sharpe Ratio measures:", opts: ["Return per unit of risk", "Total portfolio return", "Correlation between assets", "The manager's management expense ratio"], correct: 0, fb: { y: "Correct! The Sharpe Ratio is the risk-adjusted return metric — it tells you how much excess return (above the risk-free rate) was earned per unit of standard deviation (risk).", n: "Not quite. The Sharpe Ratio specifically measures return per unit of risk — how efficiently the portfolio compensated investors for the risk they took." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 8 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered MPT and portfolio management." : score >= 8 ? "Excellent. Review the questions you missed." : score >= 6 ? "Good start. Revisit correlation, beta, and the Sharpe Ratio." : "Focus on the core concepts — diversification, asset allocation, and risk metrics."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Modern Portfolio Theory (MPT)", def: "Framework for constructing a portfolio that maximizes expected return for a given level of risk through diversification." },
  { term: "Diversification", def: "Investing across assets that don't move in sync to reduce overall portfolio risk without necessarily sacrificing return." },
  { term: "Correlation", def: "A statistical measure of how two assets move in relation to each other. Ranges from −1.0 (perfectly opposite) to +1.0 (perfectly same)." },
  { term: "Positive Correlation (+1.0)", def: "Assets move in the same direction and magnitude. No diversification benefit — effectively doubling exposure." },
  { term: "Negative Correlation (−1.0)", def: "Assets move in perfectly opposite directions. Maximum diversification benefit — the ideal pairing to reduce portfolio risk." },
  { term: "Asset Allocation", def: "The most important determinant of portfolio performance. Divides capital among asset classes (cash, fixed income, equities) to match client goals." },
  { term: "Strategic Asset Allocation", def: "The long-term base mix of asset classes designed to meet client goals and risk tolerance. Rebalanced periodically." },
  { term: "Tactical Asset Allocation", def: "Short-term deviations from the strategic mix to exploit perceived market opportunities. Always reverts to the strategic base." },
  { term: "Active Management", def: "Investment approach seeking to outperform a benchmark (generate alpha) through security selection and market timing. Higher MER." },
  { term: "Passive Management", def: "Replicates a market index using index funds or ETFs. Lower MER; accepts market returns rather than trying to beat them." },
  { term: "Alpha", def: "Return earned above the benchmark — the goal of active management. A positive alpha indicates outperformance." },
  { term: "Top-Down Analysis", def: "Starts with macroeconomic analysis, then narrows to industry sectors, then individual securities." },
  { term: "Bottom-Up Analysis", def: "Focuses on individual company fundamentals regardless of the macro environment. Looks for strong companies in any market." },
  { term: "Standard Deviation", def: "Measures the variability (volatility) of returns around the average. Higher SD = higher total risk." },
  { term: "Beta", def: "Measures a security's sensitivity to market movements. Beta > 1 = more volatile (aggressive); Beta < 1 = less volatile (defensive)." },
  { term: "Sharpe Ratio", def: "Risk-adjusted return metric. (Portfolio Return − Risk-Free Rate) ÷ Standard Deviation. Higher = better return per unit of risk." },
  { term: "Real Rate of Return", def: "Nominal return minus the inflation rate. Reflects the actual increase in purchasing power after accounting for inflation." },
  { term: "Management Expense Ratio (MER)", def: "Annual fee charged to manage a fund. Active funds have higher MERs; passive index funds/ETFs have lower MERs." },
  { term: "Rebalancing", def: "Periodically restoring the portfolio to its strategic target allocation by selling outperformers and buying underperformers." },
];

// ── Main Export ──
export default function Chapter8() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Modern Portfolio Theory & Diversification"],
    ["02", "Asset Allocation — Classes & Strategy"],
    ["03", "Portfolio Management — Active vs. Passive"],
    ["04", "Measuring Portfolio Risk & Return"],
    ["🧮", "Real Return, Portfolio Return & Sharpe Ratio Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* HERO */}
        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 8
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Portfolio Theory <span style={{ color: gold }}>&amp; Management</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              Modern Portfolio Theory, diversification and correlation, asset allocation strategy, active vs. passive management, and the key risk metrics — beta, standard deviation, and the Sharpe Ratio.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","4","Core Topics"],["⏱","~18 min","read"],["🎯","10","practice questions"],["🧮","3","calculators"],["📊","2","interactive visualizers"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* TOC */}
        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* ── S1: MPT ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 01</span><span style={s.badge}>MPT & Diversification</span><div style={s.line} /></div>
            <h2 style={s.h2}>Modern Portfolio Theory</h2>
            <p style={s.prose}>
              MPT's central insight is that the <span style={{ color: gold, fontWeight: 600 }}>risk of a portfolio is less than the sum of its parts</span> — provided the assets don't move in perfect lockstep. By combining assets with low or negative correlation, you can reduce overall volatility without proportionally reducing expected return. This is the mathematical foundation of diversification.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, margin: "0 0 28px" }}>
              {[
                { corr: "+1.0", icon: "↗️↗️", color: red, label: "Perfect Positive", body: "Both assets move identically. No diversification — adding the second is redundant." },
                { corr: "0.0", icon: "↗️↔️", color: blue2, label: "Zero Correlation", body: "No relationship between the assets. Good diversification benefit." },
                { corr: "−1.0", icon: "↗️↙️", color: green, label: "Perfect Negative", body: "Assets move in exactly opposite directions. Maximum risk reduction." },
              ].map((card, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "20px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ fontFamily: "monospace", fontSize: "1.1rem", color: card.color, fontWeight: 700 }}>{card.corr}</span>
                    <span style={{ fontSize: "1.1rem" }}>{card.icon}</span>
                  </div>
                  <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white, marginBottom: 6 }}>{card.label}</div>
                  <div style={{ fontSize: "0.8rem", color: muted, lineHeight: 1.65 }}>{card.body}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}><CorrelationViz /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="🥚">
              <strong>"Don't put all your eggs in one basket"</strong> is the colloquial version of MPT. The formal version: combine assets with correlation below +1.0 to reduce portfolio variance. The lower the correlation, the greater the risk-reduction benefit from combining the assets.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S2: Asset Allocation ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 02</span><span style={s.badge}>Asset Allocation</span><div style={s.line} /></div>
            <h2 style={s.h2}>Asset Allocation</h2>
            <p style={s.prose}>
              Research shows asset allocation accounts for <span style={{ color: gold, fontWeight: 600 }}>over 90% of the variability in portfolio returns</span> — making it the single most important investment decision. It involves dividing capital among the three main asset classes based on the client's goals, time horizon, and risk tolerance.
            </p>
          </Reveal>
          <Reveal delay={0.1}><AssetClassCards /></Reveal>
          <Reveal delay={0.2}><AllocationToggle /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="🔄" accent={blue}>
              <strong style={{ color: blue2 }}>Why Rebalancing Enforces Discipline:</strong> Over time, outperforming assets grow to a larger proportion of the portfolio — drifting away from the strategic target. Rebalancing periodically means selling what has become expensive and buying what has cheapened. This systematically enforces <strong style={{ color: blue2 }}>buy low, sell high</strong> — the opposite of panic behaviour.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S3: Active vs Passive ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 03</span><span style={s.badge}>Management Styles</span><div style={s.line} /></div>
            <h2 style={s.h2}>Active vs. Passive Management</h2>
            <p style={s.prose}>
              There are two fundamentally different approaches to managing a portfolio. <span style={{ color: gold, fontWeight: 600 }}>Active management</span> attempts to beat the market through skill and research. <span style={{ color: green, fontWeight: 600 }}>Passive management</span> accepts market returns at minimal cost. The debate between them is one of the most important in modern investing:
            </p>
          </Reveal>
          <Reveal delay={0.1}><ManagementComparison /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="💡" accent={green}>
              <strong style={{ color: green }}>The Evidence:</strong> Studies consistently show that the majority of actively managed funds underperform their benchmark index over long periods, largely due to fees. A lower MER in a passive fund is a guaranteed improvement to your net return — outperformance from active management is uncertain and difficult to sustain.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S4: Risk Metrics ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 04</span><span style={s.badge}>Risk & Return Metrics</span><div style={s.line} /></div>
            <h2 style={s.h2}>Measuring Portfolio Risk &amp; Return</h2>
            <p style={s.prose}>
              Three quantitative measures are essential for Chapter 8 — standard deviation (total volatility), beta (market sensitivity), and the Sharpe Ratio (risk-adjusted return). Use the interactive beta tool and the three calculators below to build intuition before your exam.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, margin: "0 0 28px" }}>
              {[
                { icon: "📐", title: "Standard Deviation", body: "Measures total variability of returns around the average. Higher = more volatile = higher risk. Applies to the whole portfolio.", color: gold },
                { icon: "📡", title: "Beta", body: "Measures market sensitivity. Beta > 1 = aggressive (amplifies market moves). Beta < 1 = defensive (dampens moves).", color: blue2 },
                { icon: "⭐", title: "Sharpe Ratio", body: "Risk-adjusted return. How much excess return (above risk-free rate) is earned per unit of standard deviation. Higher = better.", color: green },
              ].map((card, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "20px 18px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${card.color},transparent)` }} />
                  <div style={{ fontSize: "1.4rem", marginBottom: 10 }}>{card.icon}</div>
                  <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white, marginBottom: 6 }}>{card.title}</div>
                  <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65 }}>{card.body}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}><BetaViz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Calculators ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Interactive Tools</span><span style={s.badge}>Formulas & Practice</span><div style={s.line} /></div>
            <h2 style={s.h2}>Three Chapter 8 Calculators</h2>
            <p style={s.prose}>Real Return, Portfolio Weighted Average Return, and the Sharpe Ratio — all with step-by-step working matching the exam approach.</p>
          </Reveal>
          <Reveal delay={0.1}><RealReturnCalc /></Reveal>
          <Reveal delay={0.2}><PortfolioReturnCalc /></Reveal>
          <Reveal delay={0.3}><SharpeCalc /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Quiz ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Knowledge Check</span><span style={s.badge}>10 Questions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Test Your Understanding</h2>
            <p style={s.prose}>10 questions covering MPT, asset allocation, management styles, and risk metrics from Chapter 8.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Glossary ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Quick Reference</span><span style={s.badge}>Key Terms</span><div style={s.line} /></div>
            <h2 style={s.h2}>Glossary</h2>
            <p style={s.prose}>All essential terms from Chapter 8 — portfolio theory, allocation, and risk metrics.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
